/**
 * useMilestones - 里程碑逻辑 Hook
 *
 * 负责计算里程碑列表、当前进度、下一个里程碑等。
 */

import { computed, type ComputedRef, type Ref } from 'vue-demi';
import type { MilestoneRewardItem } from '@ark/material-checkin-shared';
import { getMilestoneRewardValue } from '@ark/material-checkin-shared';
import type {
  DailyCheckinStats,
  DailyCheckinTaskProgress,
  TaskSnapshot,
} from './useCheckinJournal';

/**
 * 里程碑项
 */
export interface Milestone {
  /** 里程碑天数 */
  days: number;
  /** 奖励金额（可选，无奖励金额时为 undefined） */
  reward?: number;
  /** 奖励名称（多语言） */
  rewardName?: string;
  /** 是否已达成 */
  claimed: boolean;
}

/**
 * useMilestones Hook 配置
 */
export interface UseMilestonesOptions {
  /** 任务配置列表 */
  tasks: TaskSnapshot[] | Ref<TaskSnapshot[]>;
  /** 里程碑奖励配置 */
  milestoneRewards: MilestoneRewardItem[] | Ref<MilestoneRewardItem[]>;
  /** 每日打卡统计数据 */
  dailyCheckinStats: DailyCheckinStats | Ref<DailyCheckinStats>;
  /** 任务维度的打卡进度（累计模式专用，连续模式传 null） */
  taskProgress?: Ref<DailyCheckinTaskProgress | null>;
  /** 是否为英文环境 */
  isEnglish?: boolean | Ref<boolean>;
}

/**
 * useMilestones Hook 返回值
 */
export interface UseMilestonesReturn {
  /** 里程碑列表 */
  milestones: ComputedRef<Milestone[]>;
  /** 下一个里程碑 */
  nextMilestone: ComputedRef<Milestone | null>;
  /** 距离下一个里程碑的天数 */
  daysToNextMilestone: ComputedRef<number>;
  /** 当前展示进度值（累计模式为累计天数，连续模式为当前连续天数） */
  displayStreak: ComputedRef<number>;
  /** 判断里程碑是否已达成 */
  isMilestoneReached: (days: number) => boolean;
  /** 获取里程碑奖励金额（无奖励配置时返回 undefined） */
  getMilestoneReward: (days: number) => number | undefined;
}

/**
 * 解包 Ref 或普通值
 */
function unref<T>(value: T | Ref<T>): T {
  return (value as Ref<T>)?.value !== undefined
    ? (value as Ref<T>).value
    : (value as T);
}

/**
 * 从任务配置中提取里程碑天数列表
 */
export function extractMilestoneDays(tasks: TaskSnapshot[]): number[] {
  for (const task of tasks) {
    if (task.milestoneConfig?.milestones) {
      const milestones = task.milestoneConfig.milestones as Array<{
        value: number;
        label: string;
      }>;
      return milestones.map((m) => m.value);
    }
  }
  return [];
}

/**
 * 里程碑逻辑 Hook
 *
 * @example
 * ```typescript
 * const {
 *   milestones,
 *   nextMilestone,
 *   daysToNextMilestone,
 *   displayStreak,
 * } = useMilestones({
 *   tasks,
 *   milestoneRewards,
 *   dailyCheckinStats,
 *   isEnglish,
 * });
 * ```
 */
export function useMilestones(
  options: UseMilestonesOptions,
): UseMilestonesReturn {
  const {
    tasks,
    milestoneRewards,
    dailyCheckinStats,
    taskProgress,
    isEnglish = false,
  } = options;

  /**
   * 是否为连续模式（任意任务存在 streakConfig 即为连续模式）
   * 连续模式下沿用原有逻辑；累计模式下使用 taskProgress.currentValue
   */
  const streakResetEnabled = computed(() =>
    unref(tasks).some((t) => t.streakConfig != null),
  );

  /**
   * 当前有效进度值
   * - 连续模式：使用 dailyCheckinStats.currentStreak（原有行为）
   * - 累计模式：优先使用 taskProgress.currentValue，降级为 currentStreak
   */
  const currentValue = computed(() => {
    if (streakResetEnabled.value) {
      return unref(dailyCheckinStats).currentStreak;
    }
    return (
      taskProgress?.value?.currentValue ??
      unref(dailyCheckinStats).currentStreak
    );
  });

  /**
   * 获取里程碑奖励金额（无奖励配置时返回 undefined）
   */
  function getMilestoneReward(milestoneValue: number): number | undefined {
    const rewards = unref(milestoneRewards);
    return getMilestoneRewardValue(rewards, milestoneValue);
  }

  /**
   * 获取里程碑奖励名称（多语言）
   */
  function getMilestoneRewardName(milestoneValue: number): string | undefined {
    const rewards = unref(milestoneRewards);
    const isEn = unref(isEnglish);
    const reward = rewards.find((r) => r.days === milestoneValue);
    if (!reward?.rewardName) return undefined;
    return isEn ? reward.rewardName.en : reward.rewardName.zh;
  }

  /**
   * 判断里程碑是否已达成
   * - 连续模式：以 maxStreak 判断（原有行为，断连后仍保持已达成状态）
   * - 累计模式：以 currentValue 判断（累计天数单调递增，不会倒退）
   */
  function isMilestoneReached(milestoneDays: number): boolean {
    const stats = unref(dailyCheckinStats);
    if (streakResetEnabled.value) {
      return stats.maxStreak >= milestoneDays;
    }
    return currentValue.value >= milestoneDays;
  }

  /**
   * 里程碑列表
   */
  const milestones = computed<Milestone[]>(() => {
    const taskList = unref(tasks);
    const milestoneDays = extractMilestoneDays(taskList);

    return milestoneDays.map((days) => ({
      days,
      reward: getMilestoneReward(days),
      rewardName: getMilestoneRewardName(days),
      claimed: isMilestoneReached(days),
    }));
  });

  /**
   * 当前展示进度值
   * - 连续模式：展示 currentStreak（原有行为）
   * - 累计模式：展示 currentValue（累计天数）
   */
  const displayStreak = computed(() => {
    if (streakResetEnabled.value) {
      return unref(dailyCheckinStats).currentStreak;
    }
    return currentValue.value;
  });

  /**
   * 下一个里程碑
   */
  const nextMilestone = computed<Milestone | null>(() => {
    const streak = displayStreak.value;
    const milestoneList = milestones.value;

    if (milestoneList.length === 0) return null;

    // 找到第一个还未达成的里程碑
    const next = milestoneList.find((m) => m.days > streak);

    // 如果所有里程碑都已达成，返回最后一个
    return next || milestoneList[milestoneList.length - 1];
  });

  /**
   * 距离下一个里程碑的天数
   */
  const daysToNextMilestone = computed(() => {
    if (!nextMilestone.value) return 0;
    const streak = displayStreak.value;
    const remaining = nextMilestone.value.days - streak;
    return remaining > 0 ? remaining : 0;
  });

  return {
    milestones,
    nextMilestone,
    daysToNextMilestone,
    displayStreak,
    isMilestoneReached,
    getMilestoneReward,
  };
}

export default useMilestones;
